import
React, {
	useMemo,
	useState,
} from 'react';
import { Grid } from 'antd';
import {
	Card,
	Col,
	Row,
} from 'antd/lib';
import {
	useHistory,
	useLocation,
} from 'react-router-dom';
import { useQuery } from 'react-query';
import { GetVoyageExpensesResponse } from '@api/features/invoices/getVoyageExpenses';
import { getDisbursementAccounts, getVoyageExpense, getVoyageExpenses } from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import { useTableQuery } from '@client/utils/hooks/useTableQuery';
import DisbursementAccountModal from '@client/components/DisbursementAccountModal/DisbursementAccountModal';
import VendorExpensesTable from '../ExpenseApprovalsScreen/VendorExpensesTable';
import styles from './components/styles/ExpenseScreen.module.css';
import ExpenseDetailModal from './ExpenseDetailModal';

const ExpensesScreen = () => {
	const { userInfo } = useAuth();
	const [showAllExpenses] = useState(!userInfo.filterVessels);
	const [hideCompleted, setHideCompleted] = useState(true);
	
	const {
		data: allExpenses,
		isLoading,
		refetch,
		pagination,
		setSearch,
		onTableChange,
	} = useTableQuery<GetVoyageExpensesResponse>({
		queryKey: ['voyageExpenses', hideCompleted],
		fetcher: ({ pagination, filters, sorter, search }) => {
			const { paymentStatus: _paymentStatus, state: _state, ...serverFilters } = filters ?? {};

			return getVoyageExpenses({
				pagination,
				sorter,
				search,
				filters: serverFilters,
				hideCompleted,
			});
		},
		defaultPageSize: 100,
	});

	const {
		data: das,
		isLoading: loadingDas,
		refetch: refetchDas,
	} = useQuery(
		['portDAs', 'all'],
		() => getDisbursementAccounts(),
		{
			refetchOnWindowFocus: false,
			refetchOnMount: false,
		}
	);

	const location = useLocation();
	const history = useHistory();
	const searchParams = new URLSearchParams(location.search);
	const parsedId = searchParams.get('id');
	const parsedType = searchParams.get('type') as 'vendorExpense' | 'portDA' | null;

	const [itemType, setItemType] = useState<'vendorExpense' | 'portDA' | undefined>(parsedType ?? undefined);
	const [itemId, setItemId] = useState<number | undefined>(
		parsedId != null ? Number(parsedId) : undefined,
	);

	const {
		data: selectedExpense,
		refetch: refetchSelectedExpense
	} = useQuery(
		['voyageExpense', itemId],
		() => getVoyageExpense(itemId!),
		{
			enabled: itemId != null && itemType === 'vendorExpense',
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);

	const selectedVendorExpense = useMemo(() => {
		if (!itemId || !itemType || (itemType !== 'vendorExpense')) {
			return null;
		}

		return selectedExpense;
	}, [itemId, itemType, selectedExpense]);

	const selectedDA = useMemo(() => {
		if (itemType !== 'portDA') {
			return null;
		}

		if (das == null) {
			return null;
		}

		return das.find((d) => d.id === itemId);
	}, [das, itemType, itemId]);

	const onRefresh = () => {
		refetch();
		refetchSelectedExpense();
	};

	const screens = Grid.useBreakpoint();

	const handleItemClick = (newItemId: number, itemType: 'vendorExpense' | 'portDA') => {
		setItemId(newItemId);
		setItemType(itemType);
		history.replace(`?id=${newItemId}&type=${itemType}`);
	};

	const expenses = useMemo(() => {
		if (allExpenses === undefined || allExpenses.rows == null) {
			return [];
		}

		if (showAllExpenses) {
			return allExpenses.rows;
		}

		return allExpenses.rows.filter((e) => (
			userInfo.relevantVessels.some((v: { id: number }) => v.id === e?.vesselId)
		));
	}, [allExpenses, userInfo, showAllExpenses]);

	const combinedExpenses = useMemo(() => {
		const e = expenses ?? [];
		const d = das ?? [];

		return [...e, ...d];
	}, [expenses, das]);

	return (
		<>
			<Row gutter={[16, 16]} className={styles.expensesScreenContent}>
				<Col xxl={24} sm={24} className={screens.xs ? styles.mobileColumn : styles.column}>
					<Card>
						<VendorExpensesTable
							// @ts-ignore
							expenses={combinedExpenses}
							handleClick={(id: number, type: 'vendorExpense' | 'portDA') => handleItemClick(id, type)}
							refreshExpenses={onRefresh}
							item={selectedExpense}
							isLoading={isLoading || loadingDas}
							pagination={pagination}
							onSearch={setSearch}
							onTableChange={onTableChange}
							count={allExpenses?.count ?? 0}
							setHideCompleted={setHideCompleted}
							hideCompleted={hideCompleted}
						/>
					</Card>
				</Col>
				<ExpenseDetailModal
					item={selectedVendorExpense ?? null}
					refreshExpenses={onRefresh}
					onCancel={() => setItemId(undefined)}
					open={!!itemId}
				/>
				<DisbursementAccountModal
					onSave={() => {
						refetch();
						refetchDas();
					}}
					mode={'edit'}
					open={selectedDA != null}
					onClose={() => {
						setItemId(undefined);
						setItemType(undefined);
					}}
					disbursementAccount={selectedDA ?? undefined}
					voyageId={selectedDA?.voyageId}
				/>
			</Row>
		</>
	);
};

export default ExpensesScreen;
